'use client';
'use strict';

var jsxRuntime = require('react/jsx-runtime');
var tailwindMerge = require('tailwind-merge');
var Typography = require('../../Base/Typography/Typography.js');
require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');
var RadioCard_parts = require('./RadioCard.parts.js');
var useRadioCard = require('./useRadioCard.js');

/**
 * 라디오 카드 컴포넌트
 *
 * @description
 * 아이콘, 제목, 설명이 포함된 선택 가능한 카드 형태의 라디오 버튼입니다.
 * 가로형(wide) 또는 정사각형 형태로 사용할 수 있으며,
 * 선택, 비활성화, 에러 상태를 표현할 수 있습니다.
 *
 * @param {RadioCardProps} props
 * @param {boolean} [props.disabled=false] - 비활성화 여부
 * @param {boolean} [props.isSelected=false] - 선택 여부
 * @param {boolean} [props.isError=false] - 에러 상태 여부
 * @param {(checked: boolean) => void} props.onChange - 선택 상태 변경 시 호출되는 콜백 함수
 * @param {boolean} [props.isWide=false] - 가로형 카드 여부
 * @param {string} [props.className] - 추가 스타일 클래스
 * @param {string} props.iconName - 표시할 아이콘 이름
 * @param {string} props.title - 카드 제목
 * @param {string} [props.description] - 카드 설명
 * @param {string} [props.testId] - 테스트용 ID
 * @param {string} props.value - 라디오 버튼 값
 * @param {string} props.name - 라디오 버튼 그룹 이름
 *
 * @example
 * // 기본 사용 예시
 * <RadioCard
 *   name="payment"
 *   value="card"
 *   iconName="credit-card"
 *   title="신용카드"
 *   description="신용/체크카드로 결제"
 *   onChange={(checked) => console.log('선택됨:', checked)}
 * />
 *
 * // 가로형 카드 예시
 * <RadioCard
 *   isWide
 *   name="delivery"
 *   value="quick"
 *   iconName="truck"
 *   title="빠른배송"
 *   description="내일 오전까지 배송"
 *   onChange={(checked) => console.log('선택됨:', checked)}
 * />
 */ const RadioCard = ({ disabled = false, isSelected = false, isError = false, onChange, isWide = false, className, iconName, title, description, testId, value, name })=>{
    // TODO: useRadioCard 필요 로직만 분리하여 훅 삭제 또는 정리
    const { handleChange } = useRadioCard({
        checked: isSelected,
        disabled,
        onChange
    });
    return jsxRuntime.jsx("label", {
        "data-testid": testId,
        role: "radio-card",
        className: "group",
        children: jsxRuntime.jsxs(RadioCard_parts.RadioCardContainer, {
            "data-testid": "design-system-radioCard--container",
            isSelected: isSelected,
            isError: isError,
            isWide: isWide,
            disabled: disabled,
            className: className,
            children: [
                jsxRuntime.jsx(RadioCard_parts.StyledRadio, {
                    name: name,
                    value: value,
                    isWide: isWide,
                    isSelected: isSelected,
                    onChange: handleChange,
                    isError: isError,
                    disabled: disabled
                }),
                jsxRuntime.jsx(RadioCard_parts.RadioCardIcon, {
                    isError: isError,
                    isSelected: isSelected,
                    iconName: iconName,
                    disabled: disabled
                }),
                jsxRuntime.jsxs(RadioCard_parts.TitleContainer, {
                    isWide: isWide,
                    "data-testid": "design-system-radioCard--titleBox",
                    children: [
                        jsxRuntime.jsx(Typography, {
                            "data-testid": "design-system-radioCard--title",
                            variant: "body1",
                            className: tailwindMerge.twMerge('font-medium', isWide ? 'text-left' : 'text-center', disabled ? 'text-w-gray-300' : 'text-w-gray-900'),
                            children: title
                        }),
                        description && jsxRuntime.jsx(Typography, {
                            variant: "body2",
                            className: tailwindMerge.twMerge('font-normal', isWide ? 'text-left' : 'text-center', disabled ? 'text-w-gray-300' : 'text-w-gray-600'),
                            "data-testid": "design-system-radioCard--description",
                            children: description
                        })
                    ]
                })
            ]
        })
    });
};

module.exports = RadioCard;
